import { intlLocaleOf, type Locale } from "@/shared/i18n/messages";

/* The only place dates and numbers are formatted (ADR-0007). */

export function formatDate(value: string | Date, locale: Locale): string {
  return new Intl.DateTimeFormat(intlLocaleOf(locale), { dateStyle: "medium" }).format(
    new Date(value),
  );
}

export function formatDateTime(value: string | Date, locale: Locale): string {
  return new Intl.DateTimeFormat(intlLocaleOf(locale), {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

export function formatNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(intlLocaleOf(locale)).format(value);
}

export function formatPercent(fraction: number, locale: Locale): string {
  return new Intl.NumberFormat(intlLocaleOf(locale), { style: "percent" }).format(fraction);
}

/** "A, B and C", "أ وب وج": a list of names read as one phrase. */
export function formatList(items: readonly string[], locale: Locale): string {
  return new Intl.ListFormat(intlLocaleOf(locale), { type: "conjunction" }).format(items);
}

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 3600],
  ["month", 30 * 24 * 3600],
  ["week", 7 * 24 * 3600],
  ["day", 24 * 3600],
  ["hour", 3600],
  ["minute", 60],
];

/** "now", "3 minutes ago", "yesterday", "منذ ٣ دقائق": the largest unit that fits. */
export function formatRelative(value: string | Date, locale: Locale, now = Date.now()): string {
  const seconds = Math.round((new Date(value).getTime() - now) / 1000);
  const format = new Intl.RelativeTimeFormat(intlLocaleOf(locale), { numeric: "auto" });
  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) >= size) return format.format(Math.round(seconds / size), unit);
  }
  return format.format(0, "second");
}

/** A calendar date ("2026-10-08") as a local date, so it never shifts a day across time zones. */
function localDateOf(isoDate: string): Date {
  const [year = 0, month = 1, day = 1] = isoDate.split("-").map(Number);
  return new Date(year, month - 1, day);
}

/** A calendar date such as a due date: "8 Oct 2026", or "8 Oct" with `short`. */
export function formatDay(isoDate: string, locale: Locale, { short = false } = {}): string {
  const options: Intl.DateTimeFormatOptions = short
    ? { day: "numeric", month: "short" }
    : { day: "numeric", month: "short", year: "numeric" };
  return new Intl.DateTimeFormat(intlLocaleOf(locale), options).format(localDateOf(isoDate));
}

/**
 * Two calendar dates as one span, without repeating what they share: "8 – 20 Oct 2026",
 * "28 Sept – 4 Oct 2026". The same day twice reads as that day.
 */
export function formatDayRange(start: string, end: string, locale: Locale): string {
  const format = new Intl.DateTimeFormat(intlLocaleOf(locale), {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return format.formatRange(localDateOf(start), localDateOf(end));
}

/** A day spoken in full, for headings about today: "Thursday 8 October" / "الخميس، ٨ أكتوبر". */
export function formatLongDay(isoDate: string, locale: Locale): string {
  return new Intl.DateTimeFormat(intlLocaleOf(locale), {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(localDateOf(isoDate));
}

/** A calendar date's weekday: "Sun" / "الأحد", or "S" / "ح" with `narrow` (a grid's day header). */
export function formatWeekday(
  isoDate: string,
  locale: Locale,
  { narrow = false }: { narrow?: boolean } = {},
): string {
  return new Intl.DateTimeFormat(intlLocaleOf(locale), {
    weekday: narrow ? "narrow" : "short",
  }).format(localDateOf(isoDate));
}

/** A moment's time of day, such as a clock-in: "09:05" / "٩:٠٥ ص". */
export function formatTime(value: string | Date, locale: Locale): string {
  return new Intl.DateTimeFormat(intlLocaleOf(locale), { timeStyle: "short" }).format(
    new Date(value),
  );
}

/** A month on a timeline axis: "Oct" / "أكتوبر", or "Oct 2026" with `withYear`. */
export function formatMonth(isoDate: string, locale: Locale, { withYear = false } = {}): string {
  const options: Intl.DateTimeFormatOptions = withYear
    ? { month: "short", year: "numeric" }
    : { month: "short" };
  return new Intl.DateTimeFormat(intlLocaleOf(locale), options).format(localDateOf(isoDate));
}

const FILE_SIZE_UNITS = ["byte", "kilobyte", "megabyte", "gigabyte"] as const;
const BYTES_PER_UNIT = 1024;

/** A file's size in the largest unit that keeps it at or above 1: "820 bytes", "2.4 MB". */
export function formatFileSize(bytes: number, locale: Locale): string {
  let value = Math.max(bytes, 0);
  let unit = 0;
  while (value >= BYTES_PER_UNIT && unit < FILE_SIZE_UNITS.length - 1) {
    value /= BYTES_PER_UNIT;
    unit += 1;
  }
  return new Intl.NumberFormat(intlLocaleOf(locale), {
    style: "unit",
    unit: FILE_SIZE_UNITS[unit],
    // "820 bytes" rather than "820 byte"; larger units read best abbreviated.
    unitDisplay: unit === 0 ? "long" : "short",
    maximumFractionDigits: unit === 0 || value >= 10 ? 0 : 1,
  }).format(value);
}

/** Decimal places of each currency's minor unit (ISO 4217): piastres, cents, fils. */
const MINOR_DIGITS: Readonly<Record<string, number>> = { EGP: 2, USD: 2, AED: 2 };

/**
 * An amount in minor units as an exact decimal string ("-1234.50"), never through a float, so
 * Intl formats exactly the stored piastres.
 */
export function minorToDecimal(minor: number, currency: string): `${number}` {
  const digits = MINOR_DIGITS[currency] ?? 2;
  const sign = minor < 0 ? "-" : "";
  const absolute = String(Math.abs(Math.trunc(minor))).padStart(digits + 1, "0");
  const whole = absolute.slice(0, absolute.length - digits);
  const fraction = absolute.slice(absolute.length - digits);
  return `${sign}${whole}${digits > 0 ? `.${fraction}` : ""}` as `${number}`;
}

/**
 * Money in the reader's locale: "EGP 1,234.50", "‏١٬٢٣٤٫٥٠ ج.م.‏" (Arabic digits in Arabic).
 * `compact` shortens large amounts for tiles and chart axes ("EGP 1.2M", "١٫٢ مليون ج.م.‏").
 */
export function formatMoney(
  minor: number,
  currency: string,
  locale: Locale,
  { compact = false }: { compact?: boolean } = {},
): string {
  return new Intl.NumberFormat(intlLocaleOf(locale), {
    style: "currency",
    currency,
    ...(compact ? { notation: "compact", maximumFractionDigits: 1 } : {}),
  }).format(minorToDecimal(minor, currency));
}

/**
 * Money in a column whose header names the currency: every minor digit, no symbol ("1,234.50",
 * "١٬٢٣٤٫٥٠"). `signed` marks a change ("+250.00"); zero has no sign.
 */
export function formatAmount(
  minor: number,
  currency: string,
  locale: Locale,
  { signed = false }: { signed?: boolean } = {},
): string {
  const digits = MINOR_DIGITS[currency] ?? 2;
  return new Intl.NumberFormat(intlLocaleOf(locale), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    ...(signed ? { signDisplay: "exceptZero" as const } : {}),
  }).format(minorToDecimal(minor, currency));
}

/** An exact decimal such as an exchange rate ("48.25000000" → "48.25", "٤٨٫٢٥"). */
export function formatDecimal(
  value: string,
  locale: Locale,
  maximumFractionDigits = 4,
  { signed = false }: { signed?: boolean } = {},
): string {
  return new Intl.NumberFormat(intlLocaleOf(locale), {
    maximumFractionDigits,
    // A change such as "+2" or "−0.5"; zero has no sign.
    ...(signed ? { signDisplay: "exceptZero" as const } : {}),
  }).format(value as `${number}`);
}
